import { Menu, MenuItem, Tooltip } from '@blueprintjs/core'
import { IconNames } from '@blueprintjs/icons'
import * as React from 'react'
import { Listing, ListingAction, ListingActionDialogType } from '../../types'
import CHANNELS from '../../../../../../constants/Channels'

interface Props {
  productPurged?: boolean
  channel: any
  listing: Listing
  onActionChange: (a: ListingActionDialogType) => void
}

const ListingActionMenu: React.FC<Props> = ({ channel, listing, onActionChange, productPurged }) => {
  const deleteable = ['PENDING', 'LISTING_FAILED', 'RELISTING_FAILED'].includes(listing.state)
  const listable = ['PENDING', 'LISTING_FAILED'].includes(listing.state)
  const delistable = ['LISTED', 'DELISTING_FAILED', 'MODIFY_FAILED'].includes(listing.state)
  const relistable =
    ['DELISTED', 'EXPIRED', 'PURGED', 'PENDING_RELISTING', 'RELISTING_FAILED', 'SOLD'].includes(listing.state) &&
    !productPurged
  // We could check # of bids here and disable this action if there are zero bids, since they could just edit the listing in that case.
  // But there's no need to be opinionated, we can let them do it this way if they'd rather.
  const canAddAdditionalInfo = ['LISTED'].includes(listing.state)

  let relistTooltipContent = null
  const isSgw = channel.id === CHANNELS.SHOPGOODWILL.id
  const isEbay = channel.id === CHANNELS.EBAY.id

  if (!relistable) {
    if (productPurged) {
      relistTooltipContent = "This listing's product has been purged. It cannot be relisted"
    } else {
      relistTooltipContent = 'Can only relist expired, sold, or delisted listings.'
      if (isSgw) {
        relistTooltipContent += ' Try recycling first.'
      }
    }
  }

  let recycleable = ['DELISTED', 'EXPIRED', 'PURGING_FAILED'].includes(listing.state)
  if (isSgw) {
    recycleable = ['DELISTED', 'EXPIRED', 'PURGING_FAILED', 'SOLD', 'UNPAID'].includes(listing.state)
  }

  return (
    <Menu>
      {listable && (
        <MenuItem
          icon={IconNames.UPLOAD}
          text="List"
          onClick={() => onActionChange({ actionType: ListingAction.LIST, listing })}
        />
      )}

      <MenuItem
        disabled={!delistable}
        onClick={() => onActionChange({ actionType: ListingAction.DELIST, listing })}
        icon={IconNames.TAKE_ACTION}
        text={<Tooltip content={!delistable && 'Can only delist listed listings'}>End early</Tooltip>}
      />

      {(isSgw || isEbay) && (
        <MenuItem
          disabled={!canAddAdditionalInfo}
          onClick={() => onActionChange({ actionType: ListingAction.ADD_ADDITIONAL_INFO, listing })}
          icon="bring-data"
          text={
            <Tooltip content={!canAddAdditionalInfo && 'Can only add additional info to listed listings'}>
              Add additional info
            </Tooltip>
          }
        />
      )}

      <MenuItem
        disabled={!relistable}
        onClick={() => onActionChange({ actionType: ListingAction.RELIST, listing })}
        icon={IconNames.REFRESH}
        text={<Tooltip content={relistTooltipContent}>Relist</Tooltip>}
      />
      <MenuItem
        onClick={() => onActionChange({ actionType: ListingAction.RECYCLE, listing })}
        disabled={!recycleable}
        icon={IconNames.REPEAT}
        text={<Tooltip content={!recycleable && 'Can only recycle delisted or expired listings'}>Recycle</Tooltip>}
      />
      <MenuItem
        onClick={() => onActionChange({ actionType: ListingAction.DELETE, listing })}
        disabled={!deleteable}
        icon={IconNames.TRASH}
        text={<Tooltip content={!deleteable && 'Can only delete failed or pending listings'}>Delete</Tooltip>}
      />
    </Menu>
  )
}

export default ListingActionMenu
